import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/authContext'
import { useSubscriptions } from '../hooks/useApi'
import { canAccessMarketingCenter, normalizePlanId } from '../lib/plans'
import { Button } from './ui/Button'
import { CatalogPdfDownload } from './CatalogPdfDownload'
import logoUmkm from '../assets/logo-umkm.png'
import { Menu, X, Settings, UserCog, KeyRound, ChevronDown, MessageCircle, Megaphone } from 'lucide-react'

const WHATSAPP_NUMBER = '6282125651489'
const WHATSAPP_MESSAGE =
  'Halo, saya tertarik untuk mendaftar di platform UMKM Indonesia. Mohon informasi lebih lanjut mengenai cara pendaftaran dan paket yang tersedia. Terima kasih.'

const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`

function HubungiKamiButton({ compact = false, className = '' }: { compact?: boolean; className?: string }) {
  return (
    <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className={className}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={`h-9 px-2.5 sm:px-3 border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800 hover:border-green-300 ${
          compact ? 'min-w-9' : ''
        }`}
        aria-label="Hubungi Kami via WhatsApp"
      >
        <MessageCircle className="h-4 w-4 shrink-0" />
        {!compact && <span className="ml-1.5 hidden md:inline">Hubungi Kami</span>}
      </Button>
    </a>
  )
}

const navLinks = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/brand', label: 'Brand Saya' },
  { to: '/products', label: 'Produk' },
  { to: '/marketing', label: 'Marketing Center' },
  { to: '/artikel-usaha', label: 'Artikel Usaha' },
  { to: '/subscription', label: 'Paket' },
  { to: '/testimoni', label: 'Testimoni' },
  { to: '/catalog', label: 'Katalog UMKM' },
  { to: '/artikel', label: 'Artikel' },
] as const

const guestLinks = [
  { to: '/catalog', label: 'Katalog UMKM' },
  { to: '/artikel', label: 'Artikel' },
  { to: '/login', label: 'Login' },
  { to: '/register', label: 'Daftar' },
] as const

export function Navbar() {
  const { user, logout } = useAuth()
  const { data: subscriptions } = useSubscriptions(!!user)
  const currentSub = subscriptions?.[0]
  const planId = normalizePlanId(currentSub?.isActive !== false ? currentSub?.plan : 'free')
  const hasMarketingAccess = canAccessMarketingCenter(planId)

  const userNavLinks = navLinks.filter((link) => link.to !== '/artikel-usaha')
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setSettingsOpen(false)
    setMobileSettingsOpen(false)
  }, [location.pathname])

  const handleLogout = () => {
    setMobileOpen(false)
    logout()
    navigate('/login')
  }

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`)

  return (
    <nav
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-indigo-200/80'
          : 'bg-gradient-to-r from-indigo-600 via-indigo-500 to-blue-600 border-indigo-500/30 shadow-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center min-h-14 sm:min-h-16 gap-2 py-2">
          <div className="flex items-center min-w-0 shrink-0">
            <Link
              to={user ? '/dashboard' : '/'}
              className={`flex items-center justify-center shrink-0 rounded-full p-0.5 transition-shadow ${
                scrolled
                  ? 'bg-white ring-1 ring-indigo-100 shadow-sm'
                  : 'bg-white/95 ring-2 ring-white/40 shadow-md'
              }`}
              title="UMKM Indonesia"
            >
              <img
                src={logoUmkm}
                alt="UMKM Indonesia"
                className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain"
              />
            </Link>
          </div>

          {user && (
            <div className="hidden lg:flex flex-1 ml-8 items-center flex-wrap gap-x-0 gap-y-1 min-w-0">
              {userNavLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`px-2.5 py-2 text-sm whitespace-nowrap rounded-md transition-colors inline-flex items-center gap-1 ${
                    to === '/marketing' && hasMarketingAccess
                      ? scrolled
                        ? 'text-white bg-indigo-600 hover:bg-indigo-700 font-medium shadow-sm'
                        : 'text-indigo-700 bg-white hover:bg-white/90 font-medium shadow-sm'
                      : isActive(to)
                        ? scrolled
                          ? 'text-indigo-700 bg-indigo-50 font-medium'
                          : 'text-white bg-white/20 font-medium'
                        : scrolled
                          ? 'text-gray-600 hover:text-indigo-700 hover:bg-indigo-50/50'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {to === '/marketing' && <Megaphone className="w-4 h-4 shrink-0" />}
                  {label}
                </Link>
              ))}
              <CatalogPdfDownload lightText={!scrolled} />
            </div>
          )}

          {!user && (
            <div className="hidden md:flex flex-1 ml-6 items-center gap-1">
              <Link
                to="/catalog"
                className={`px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors ${
                  isActive('/catalog')
                    ? scrolled
                      ? 'text-indigo-700 bg-indigo-50 font-medium'
                      : 'text-white bg-white/20 font-medium'
                    : scrolled
                      ? 'text-gray-600 hover:text-indigo-700'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Katalog UMKM
              </Link>
              <Link
                to="/artikel"
                className={`px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors ${
                  isActive('/artikel')
                    ? scrolled
                      ? 'text-indigo-700 bg-indigo-50 font-medium'
                      : 'text-white bg-white/20 font-medium'
                    : scrolled
                      ? 'text-gray-600 hover:text-indigo-700'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Artikel
              </Link>
            </div>
          )}

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {user ? (
              <>
                <span className={`hidden xl:inline text-sm truncate max-w-[140px] ${scrolled ? 'text-gray-700' : 'text-white/95'}`}>
                  {user.name}
                </span>
                <div className="relative hidden lg:block">
                  <button
                    type="button"
                    onClick={() => setSettingsOpen((v) => !v)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-2 text-sm border rounded-md transition-colors ${
                      scrolled
                        ? 'text-gray-600 hover:text-gray-900 border-gray-200 hover:bg-gray-50'
                        : 'text-white border-white/30 hover:bg-white/10'
                    }`}
                  >
                    <Settings className="h-4 w-4" />
                    Pengaturan
                    <ChevronDown className={`h-4 w-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {settingsOpen && (
                    <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg z-50 p-1">
                      {user && (
                        <Link
                          to="/marketing"
                          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-indigo-700 hover:bg-indigo-50 font-medium"
                        >
                          <Megaphone className="h-4 w-4" />
                          Marketing Center
                        </Link>
                      )}
                      <Link
                        to="/settings?tab=profile"
                        className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        <UserCog className="h-4 w-4" />
                        Update Profil
                      </Link>
                      <Link
                        to="/settings?tab=password"
                        className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        <KeyRound className="h-4 w-4" />
                        Ubah Password
                      </Link>
                    </div>
                  )}
                </div>
                <Button variant="outline" onClick={handleLogout} className="hidden lg:inline-flex h-9 text-sm">
                  Logout
                </Button>
                <button
                  type="button"
                  className={`lg:hidden inline-flex items-center justify-center rounded-md p-2 ${
                    scrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileOpen((o) => !o)}
                  aria-expanded={mobileOpen}
                  aria-label={mobileOpen ? 'Tutup menu' : 'Buka menu'}
                >
                  {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </>
            ) : (
              <>
                <div className="hidden md:flex items-center gap-2">
                  <HubungiKamiButton />
                  <Link to="/login">
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-9 ${scrolled ? '' : 'border-white/40 text-white hover:bg-white/10 hover:text-white'}`}
                    >
                      Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" className={`h-9 ${scrolled ? '' : 'bg-white text-indigo-700 hover:bg-white/90'}`}>
                      Daftar
                    </Button>
                  </Link>
                </div>
                <div className="md:hidden flex items-center gap-1">
                  <HubungiKamiButton compact />
                  <button
                    type="button"
                    className={`md:hidden inline-flex items-center justify-center rounded-md p-2 ${
                      scrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white hover:bg-white/10'
                    }`}
                    onClick={() => setMobileOpen((o) => !o)}
                    aria-expanded={mobileOpen}
                    aria-label={mobileOpen ? 'Tutup menu' : 'Buka menu'}
                  >
                    {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {mobileOpen && user && (
          <div className="lg:hidden border-t border-gray-100 py-3 space-y-1 bg-white">
            {userNavLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`block rounded-lg px-3 py-2.5 text-base font-medium ${
                  isActive(to) ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {label}
              </Link>
            ))}
            <CatalogPdfDownload mobileBlock />
            <button
              type="button"
              onClick={() => setMobileSettingsOpen((v) => !v)}
              className="w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50"
            >
              <span className="inline-flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Pengaturan
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform ${mobileSettingsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileSettingsOpen && (
              <div className="space-y-1 px-3">
                {user && (
                  <Link to="/marketing" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50 font-medium">
                    <Megaphone className="h-4 w-4" />
                    Marketing Center
                  </Link>
                )}
                <Link to="/settings?tab=profile" className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  Update Profil
                </Link>
                <Link to="/settings?tab=password" className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  Ubah Password
                </Link>
              </div>
            )}
            <div className="pt-3 mt-2 border-t border-gray-100 px-3">
              <p className="text-sm text-gray-500 truncate mb-2">{user.name}</p>
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        )}

        {mobileOpen && !user && (
          <div className="md:hidden border-t border-gray-100 py-3 space-y-1 bg-white px-1">
            {guestLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`block rounded-lg px-3 py-2.5 text-base font-medium ${
                  isActive(to) ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {label}
              </Link>
            ))}
            <a
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-base font-medium text-green-700 hover:bg-green-50"
            >
              <MessageCircle className="h-5 w-5" />
              Hubungi Kami (WhatsApp)
            </a>
          </div>
        )}
      </div>
    </nav>
  )
}
